import { Check,Pencil, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Tooltip } from "src/components/ui/tooltip";
import type { DashboardCard } from "src/types/dashboard";

interface DashboardCardInputProps {
  placeholder: string;
  className?: string;
  cards: DashboardCard[];
  onChange: (cards: DashboardCard[]) => void;
  resourceType?: "dashboard" | "notebook" | "notion";
}

const isUrl = (value: string): boolean => {
  return value.trim().startsWith("http://") || value.trim().startsWith("https://");
};

const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

export function DashboardCardInput({
  placeholder,
  className = "bg-card border-border",
  cards,
  onChange,
  resourceType = "dashboard",
}: DashboardCardInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [editValidationError, setEditValidationError] = useState<string | null>(null);
  const [cardIdCounter, setCardIdCounter] = useState(0);

  const getTypeLabel = (type: "id" | "url"): string => {
    if (type === "url") {
      return "URL";
    }

    switch (resourceType) {
      case "dashboard":
        return "Dashboard ID";
      case "notebook":
        return "Notebook ID";
      case "notion":
        return "URL"; // Notion should only have URLs
      default:
        return "ID";
    }
  };

  const validateValue = (value: string, excludeCardId?: string): string | null => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null; // Empty is handled elsewhere
    }

    // For Notion, only URLs are allowed
    if (resourceType === "notion" && !isUrl(trimmedValue)) {
      return "Notion links must be URLs starting with http:// or https://";
    }

    // Check for spaces in the middle (after trimming)
    if (trimmedValue.includes(" ")) {
      return "Value cannot contain spaces";
    }

    // Check for duplicates (case-insensitive), optionally excluding a specific card (for edits)
    const isDuplicate = cards.some(
      (card) => card.id !== excludeCardId && card.value.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (isDuplicate) {
      return "This value already exists in the list";
    }

    return null;
  };

  const handleAdd = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    const error = validateValue(trimmedValue);
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError(null);

    const newCard: DashboardCard = {
      id: `card-${Date.now()}-${cardIdCounter}`,
      value: trimmedValue,
      type: isUrl(trimmedValue) ? "url" : "id",
    };

    setCardIdCounter(cardIdCounter + 1);
    onChange([...cards, newCard]);
    setInputValue("");
  };

  const handleDelete = (cardId: string) => {
    onChange(cards.filter((card) => card.id !== cardId));
  };

  const handleStartEdit = (card: DashboardCard) => {
    setEditingId(card.id);
    setEditValue(card.value);
    setEditValidationError(null);
  };

  const handleSaveEdit = (cardId: string) => {
    const trimmedValue = editValue.trim();
    if (!trimmedValue) {
      handleCancelEdit();
      return;
    }

    // Run full validation (excluding current card from duplicate check)
    const error = validateValue(trimmedValue, cardId);
    if (error) {
      setEditValidationError(error);
      return;
    }

    onChange(
      cards.map((card) =>
        card.id === cardId
          ? { ...card, value: trimmedValue, type: isUrl(trimmedValue) ? "url" : "id" }
          : card
      )
    );
    setEditingId(null);
    setEditValue("");
    setEditValidationError(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
    setEditValidationError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, cardId: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit(cardId);
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  return (
    <div className="space-y-3">
      {/* Input field with Add button */}
      <div>
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setValidationError(null); // Clear error on input change
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`flex-1 ${className} text-foreground placeholder:text-muted-foreground ${
              validationError ? "border-red-500" : ""
            }`}
          />
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!inputValue.trim()}
            variant="outline"
            className="shrink-0 h-10"
          >
            Add
          </Button>
        </div>
        {validationError && (
          <div className="text-xs text-red-400 mt-1">
            {validationError}
          </div>
        )}
      </div>

      {/* Cards list */}
      {cards.length > 0 && (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {cards.map((card) => (
            <div
              key={card.id}
              className="flex items-center gap-2 p-3 bg-card rounded-md border border-border"
            >
              {editingId === card.id ? (
                <div className="flex-1 flex flex-col">
                  <div className="flex items-center gap-2">
                    <Input
                      value={editValue}
                      onChange={(e) => {
                        setEditValue(e.target.value);
                        setEditValidationError(null); // Clear error on input change
                      }}
                      onKeyDown={(e) => handleEditKeyDown(e, card.id)}
                      className={`flex-1 bg-secondary border-border text-foreground ${
                        editValidationError ? "border-red-500" : ""
                      }`}
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSaveEdit(card.id)}
                      className="shrink-0 h-8 w-8 p-0 hover:bg-muted"
                    >
                      <Check className="h-4 w-4 text-green-400" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="shrink-0 h-8 w-8 p-0 hover:bg-muted"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                  {editValidationError && (
                    <div className="text-xs text-red-400 mt-1">
                      {editValidationError}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="text-xs text-muted-foreground mb-1">
                      {getTypeLabel(card.type)}
                    </div>
                    <div className="text-sm text-foreground break-all" title={card.value}>
                      {truncateText(card.value, 100)}
                    </div>
                  </div>
                  <Tooltip content="Edit" position="top">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStartEdit(card)}
                      className="shrink-0 h-8 w-8 p-0 hover:bg-muted"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Delete" position="top">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(card.id)}
                      className="shrink-0 h-8 w-8 p-0 hover:bg-red-100 dark:hover:bg-red-900/30"
                    >
                      <Trash2 className="h-4 w-4 text-red-500 dark:text-red-400" />
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
